import React, { useState, useEffect, useMemo } from 'react';
import styles from './SectionParticles.module.css';

/**
 * SectionParticles - Partículas de fondo para secciones
 * OPTIMIZADO: Renderiza en todas las resoluciones con densidades adaptativas
 * Múltiples tipos de partículas para efectos variados
 */
function SectionParticles({ 
  count = 20, 
  color = '#00ff00',
  type = 'float', // 'float', 'rise', 'glow', 'mixed'
  intensity = 'medium' // 'low', 'medium', 'high'
}) {
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // Detectar cambios de viewport con debounce
  useEffect(() => {
    let debounceTimer;
    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setViewportWidth(window.innerWidth);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(debounceTimer);
    };
  }, []);

  // Calcular densidad adaptativa según viewport
  const adaptiveCount = useMemo(() => {
    if (viewportWidth <= 480) return Math.min(count * 0.3, 8); // Mobile: 30%
    if (viewportWidth <= 768) return Math.min(count * 0.5, 12); // Tablet: 50%
    if (viewportWidth <= 1024) return Math.min(count * 0.7, 18); // Laptop: 70%
    return count; // Desktop: 100%
  }, [viewportWidth, count]);

  // Generar partículas con diferentes tipos
  const particles = useMemo(() => {
    const newParticles = [];
    
    for (let i = 0; i < adaptiveCount; i++) {
      const particleType = type === 'mixed' 
        ? ['float', 'rise', 'glow'][Math.floor(Math.random() * 3)]
        : type;
      
      // Variaciones de tamaño según intensidad
      const sizeMultiplier = {
        low: 0.6,
        medium: 1,
        high: 1.4
      }[intensity];

      newParticles.push({
        id: i,
        left: Math.random() * 100,
        top: particleType === 'rise' ? 100 + Math.random() * 20 : Math.random() * 100,
        delay: Math.random() * 4,
        duration: particleType === 'rise' ? 8 + Math.random() * 4 : 4 + Math.random() * 3,
        size: (1.5 + Math.random() * 2) * sizeMultiplier,
        type: particleType,
        opacity: 0.4 + Math.random() * 0.4,
      });
    }
    
    return newParticles;
  }, [adaptiveCount, type, intensity]);

  if (particles.length === 0) return null;

  return (
    <div className={styles.particlesContainer}>
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`${styles.particle} ${styles[particle.type]}`}
          style={{
            left: `${particle.left}%`,
            top: `${particle.top}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            '--particle-delay': `${particle.delay}s`,
            '--particle-duration': `${particle.duration}s`,
            '--particle-color': color,
            '--particle-opacity': particle.opacity,
          }}
        ></div>
      ))}
    </div>
  );
}

export default React.memo(SectionParticles);
